import { message } from "@/utils/staticAntd";
import {
  BugOutlined,
  CameraOutlined,
  DislikeOutlined,
  MessageOutlined,
  PlusOutlined,
  RocketOutlined,
  StarOutlined,
} from "@ant-design/icons";
import { useIntl } from "@umijs/max";
import { Form, Input, Modal, Radio, Upload } from "antd";
import type { UploadFile } from "antd";
import React, { useCallback, useState } from "react";
import {
  submitFeedback,
  uploadFeedbackScreenshot,
} from "@/services/stockware/feedback";

interface FeedbackModalProps {
  open: boolean;
  onCancel: () => void;
}

const CATEGORY_OPTIONS = [
  { value: "BUG", icon: <BugOutlined /> },
  { value: "SUGGESTION", icon: <MessageOutlined /> },
  { value: "FEATURE", icon: <RocketOutlined /> },
  { value: "DISSATISFIED", icon: <DislikeOutlined /> },
];

const MAX_SCREENSHOTS = 5;
const MAX_SIZE_KB = 90;

/** 压缩图片到指定大小 */
function compressImage(file: File, maxKB: number = MAX_SIZE_KB): Promise<File> {
  return new Promise((resolve, reject) => {
    // 已经足够小，直接返回
    if (file.size <= maxKB * 1024) {
      resolve(file);
      return;
    }

    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");

      // 大图先缩小尺寸
      let { width, height } = img;
      const maxDim = 1920;
      if (width > maxDim || height > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0, width, height);

      // 逐步降低质量直到满足大小要求
      let quality = 0.8;
      const tryCompress = () => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("压缩失败"));
              return;
            }
            if (blob.size <= maxKB * 1024 || quality <= 0.1) {
              resolve(
                new File([blob], file.name.replace(/\.\w+$/, ".jpg"), {
                  type: "image/jpeg",
                })
              );
            } else {
              quality -= 0.05;
              tryCompress();
            }
          },
          "image/jpeg",
          quality
        );
      };
      tryCompress();
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("图片加载失败"));
    };
    img.src = url;
  });
}

/** 收集浏览器信息 */
function collectBrowserInfo(): string {
  const { userAgent, language } = navigator;
  const { width, height } = window.screen;
  return `${userAgent} | ${language} | ${width}x${height}`;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ open, onCancel }) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      // 压缩并上传截图
      const screenshotUrls: string[] = [];
      for (const file of fileList) {
        if (file.url) {
          screenshotUrls.push(file.url);
        } else if (file.originFileObj) {
          const compressed = await compressImage(file.originFileObj);
          const url = await uploadFeedbackScreenshot(compressed);
          screenshotUrls.push(url);
        }
      }

      await submitFeedback({
        category: values.category,
        content: values.content,
        contactEmail: values.contactEmail || undefined,
        pageUrl: window.location.pathname,
        browserInfo: collectBrowserInfo(),
        screenshotUrls,
      });

      message.success(intl.formatMessage({ id: "feedback.submit.success" }));
      form.resetFields();
      setFileList([]);
      onCancel();
    } catch (err: any) {
      if (err?.errorFields) return; // form validation error
    } finally {
      setSubmitting(false);
    }
  }, [form, fileList, intl, onCancel]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    setFileList([]);
    onCancel();
  }, [form, onCancel]);

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={submitting}
      okText={intl.formatMessage({ id: "feedback.submit" })}
      cancelText={intl.formatMessage({ id: "common.cancel" })}
      width={520}
      centered
      destroyOnHidden
      title={
        <span>
          <StarOutlined style={{ marginRight: 8 }} />
          {intl.formatMessage({ id: "feedback.title" })}
        </span>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ category: "SUGGESTION" }}
        style={{ paddingTop: 16 }}
      >
        <Form.Item
          name="category"
          label={intl.formatMessage({ id: "feedback.category" })}
          rules={[{ required: true }]}
        >
          <Radio.Group optionType="button" buttonStyle="solid">
            {CATEGORY_OPTIONS.map((opt) => (
              <Radio.Button key={opt.value} value={opt.value}>
                {opt.icon}{" "}
                {intl.formatMessage({
                  id: `feedback.category.${opt.value.toLowerCase()}`,
                })}
              </Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="content"
          label={intl.formatMessage({ id: "feedback.content" })}
          rules={[
            {
              required: true,
              message: intl.formatMessage({ id: "feedback.content.required" }),
            },
          ]}
        >
          <Input.TextArea
            rows={4}
            maxLength={2000}
            showCount
            placeholder={intl.formatMessage({
              id: "feedback.content.placeholder",
            })}
          />
        </Form.Item>

        <Form.Item label={intl.formatMessage({ id: "feedback.screenshot" })}>
          <Upload
            listType="picture-card"
            fileList={fileList}
            onChange={({ fileList: newList }) => setFileList(newList)}
            beforeUpload={(file) => {
              const isImage = file.type.startsWith("image/");
              if (!isImage) {
                message.error(
                  intl.formatMessage({ id: "feedback.screenshot.typeError" })
                );
                return Upload.LIST_IGNORE;
              }
              return false; // 不自动上传，提交时统一压缩上传
            }}
            maxCount={MAX_SCREENSHOTS}
            accept="image/*"
          >
            {fileList.length < MAX_SCREENSHOTS && (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 4, fontSize: 12 }}>
                  <CameraOutlined />{" "}
                  {intl.formatMessage({ id: "feedback.screenshot.add" })}
                </div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Form.Item
          name="contactEmail"
          label={intl.formatMessage({ id: "feedback.contactEmail" })}
          rules={[
            {
              type: "email",
              message: intl.formatMessage({
                id: "feedback.contactEmail.invalid",
              }),
            },
          ]}
        >
          <Input
            placeholder={intl.formatMessage({
              id: "feedback.contactEmail.placeholder",
            })}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default FeedbackModal;
